import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import flow from 'lodash/flow';
import get from 'lodash/get';
import ValueCircle from 'src/common/components/shared/ValueCircle';
import PillButton from 'src/common/components/shared/button/PillButton';
import {
  UNLOCK_DAYS_REMAINING,
  UNLOCK_HOURS_REMAINING,
  UNLOCK_MINUTES_REMAINING,
  UNLOCK_SUBMIT_TEXT,
  UNLOCK_SUCCESS_TEXT,
} from 'src/common/constants/localizations/unlock';
import getGuideItemPathname from '../../utils/guideItem/getGuideItemPathname';
import {
  PREMIUM_UNLOCK_CLICK_ERROR,
  PREMIUM_UNLOCK_REDIRECT,
} from '../../constants/ads/logging';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { logClientError, logClientInfo } from '../../actions/logging';
import calculateTimeRemaining from './utils/calculateTimeRemaining';
import unlockCss from './unlock.module.scss';

const styles = {
  buttonContainer: {
    marginTop: 20,
  },
};

class UnlockSuccessView extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    unlockActions: PropTypes.shape({
      unlockClosed: PropTypes.func.isRequired,
    }),
    logging: PropTypes.shape({
      logClientError: PropTypes.func.isRequired,
      logClientInfo: PropTypes.func.isRequired,
    }),
    secondsRemaining: PropTypes.number,
    actionGuideId: PropTypes.string,
  };

  static contextType = LocationAndLocalizationContext;

  handleClick = () => {
    const { unlockActions, history, actionGuideId, logging, secondsRemaining } =
      this.props;
    try {
      unlockActions.unlockClosed();

      // secondsRemaining here refers to the amount of free premium time that the user
      // has after watching the Premium Unlock Ad. We want to route the user to
      // /radio/premium only if they still have free premium time left.
      if (!actionGuideId || !secondsRemaining || secondsRemaining <= 0) {
        return;
      }

      const redirectUrl = getGuideItemPathname({ guideId: actionGuideId });

      if (!redirectUrl) {
        return;
      }

      history.push({
        pathname: redirectUrl,
        query: get(this.context, 'location.query', {}),
      });

      logging.logClientInfo({
        message: PREMIUM_UNLOCK_REDIRECT,
        context: { redirectUrl },
      });
    } catch (ex) {
      logging.logClientError({
        message: PREMIUM_UNLOCK_CLICK_ERROR,
        context: {
          error: ex,
        },
      });
    }
  };

  render() {
    const { secondsRemaining } = this.props;
    const { getLocalizedText } = this.context;
    const timeRemaining = calculateTimeRemaining(secondsRemaining);

    let timeRemainingLocalization;
    if (timeRemaining) {
      switch (timeRemaining.unit) {
        case 'days':
          timeRemainingLocalization = UNLOCK_DAYS_REMAINING;
          break;
        case 'hours':
          timeRemainingLocalization = UNLOCK_HOURS_REMAINING;
          break;
        case 'minutes':
          timeRemainingLocalization = UNLOCK_MINUTES_REMAINING;
          break;
        default:
      }
    }

    return (
      <div data-testid="unlockSuccess">
        <p className={unlockCss.title}>
          {getLocalizedText(UNLOCK_SUCCESS_TEXT)}
        </p>
        {timeRemaining !== undefined && (
          <div>
            <ValueCircle
              id="unlockSuccessValueCircle"
              value={timeRemaining.value.toString()}
            />
            <div className={unlockCss.timeRemaining}>
              <span>{getLocalizedText(timeRemainingLocalization)}</span>
            </div>
          </div>
        )}
        <div className={unlockCss.section}>
          <div className="row">
            <div
              className="col-xs-12 col-sm-offset-3 col-sm-6 col-md-offset-1 col-md-10"
              style={styles.buttonContainer}
            >
              <PillButton
                id="unlockOkayButton"
                type="submit"
                label={getLocalizedText(UNLOCK_SUBMIT_TEXT)}
                onClick={this.handleClick}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { secondsRemaining, actionGuideId } = state.unlockDetails;

  return {
    secondsRemaining,
    actionGuideId,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    logging: bindActionCreators({ logClientInfo, logClientError }, dispatch),
  };
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
)(UnlockSuccessView);
